import {noop} from '@benchling/js-utils';
import PropTypes from 'prop-types';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {OptionHeight} from './AttachDropdownConstants';
import {AttachDropdownOptionContainer} from './AttachDropdownOptionsMenu';

/**
 * Components for the menu that is rendered by AttachDropdown when there are no search results. Will always
 * render an AttachDropdownNotice, which either provides an informational message or a loading throbber. Can
 * also render an optional menu item that has custom on-click and rendering behavior.
 */

interface AttachDropdownNoticeProps {
  closeHandler: (() => void) | null;
  children: JSX.Element | string;
}

export function AttachDropdownNotice({closeHandler, children}: AttachDropdownNoticeProps): JSX.Element {
  const preventDefault = (e: BasicEvent): void => {
    e.preventDefault();
  };

  return (
    <li
      className="attachDropdown-notice"
      onMouseDown={preventDefault}
      {...getTestProps({component: 'AttachDropdownNoResultsMenu', element: 'notice'})}
    >
      {children}
      {closeHandler ? (
        <span className="pull-right faded u-pointer" onClick={closeHandler}>
          <BenchlingIcon icon="close" />
        </span>
      ) : null}
    </li>
  );
}
AttachDropdownNotice.propTypes = {
  closeHandler: PropTypes.func,
  children: PropTypes.node,
};

interface AttachDropdownNoResultsMenuItem {
  ref: React.Ref<AttachDropdownOptionContainer>;
  index: number;
  onHover: () => void;
  onClick: () => void;
  menuItem: JSX.Element | null;
}

interface AttachDropdownNoResultsMenuProps {
  headerContents: JSX.Element | string;
  close: (() => void) | null;
  hoverIndex: number;
  height: OptionHeight;
  noResultsItems: Array<AttachDropdownNoResultsMenuItem>;
}

export default function AttachDropdownNoResultsMenu({
  headerContents,
  close,
  hoverIndex,
  height,
  noResultsItems,
}: AttachDropdownNoResultsMenuProps): JSX.Element {
  return (
    <>
      <AttachDropdownNotice closeHandler={close}>{headerContents}</AttachDropdownNotice>
      {noResultsItems.map(({ref, index, menuItem, onHover, onClick}, noResultsIndex) =>
        menuItem ? (
          <AttachDropdownOptionContainer
            key={index}
            ref={ref}
            isHovered={hoverIndex === index}
            index={index}
            height={height}
            result={null}
            selectAttachment={noop}
            onHover={onHover}
            onClick={onClick}
          >
            {menuItem}
          </AttachDropdownOptionContainer>
        ) : null
      )}
    </>
  );
}
AttachDropdownNoResultsMenu.propTypes = {
  headerContents: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  close: PropTypes.func,
  hoverIndex: PropTypes.number.isRequired,
  height: PropTypes.oneOf(Object.values(OptionHeight)).isRequired,
  noResultsItems: PropTypes.arrayOf(PropTypes.object),
};
